import { Organizations } from "@knowfully/ui.common";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PublishIcon from "@mui/icons-material/Publish";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Fab,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import BasicActionsThreeDotsMenu from "components/ThreeDots/BasicActionsThreeDotsMenu";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useRequirementFilterOptions from "hooks/useRequirementFilterOptions";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { ReactNode, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  Navigate,
  Link as RouterLink,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ILicenseQuestionDetail,
  IRequirementListItem,
  ListRequestOfRequirementAdminFilter,
  RequirementAdminFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import { hasUserPermission } from "helpers/ClaimHelpers";
import {
  ClaimPermission,
  ClaimResource,
  ParentClaim,
} from "types/claimTypes";
import LicenseQuestionForm from "./LicenseQuestionForm";

const ActionsCell = ({ value }: { value: any }) =>
  value.onEdit || value.onDelete ? (
    <Box display="flex" justifyContent="flex-end">
      <BasicActionsThreeDotsMenu
        onEdit={value.onEdit}
        onDelete={value.onDelete}
      />
    </Box>
  ) : null;

const RequiredCell = ({ value }: { value: any }) => {
  const { formatMessage: t } = useIntl();
  const q = value as ILicenseQuestionDetail;
  return (
    <Chip
      size="small"
      color={q.required ? "primary" : "default"}
      label={
        q.required ? t({ id: "requiredLabel" }) : t({ id: "optionalLabel" })
      }
    />
  );
};

const LicenseDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    licenseService,
    licensesUrl,
    requirementSetService,
    requirementService,
    requirementSetDetailsUrl,
    requirementDetailsUrl,
    licenseAuthoringUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const queryClient = useQueryClient();
  const [editRow, setEditRow] = useState<ILicenseQuestionDetail | null>(null);
  const [editOpen, editOpenActions] = useBoolean(false);
  const [deleteRow, setDeleteRow] = useState<ILicenseQuestionDetail | null>(
    null,
  );
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [requirementSearch, setRequirementSearch] = useState("");
  const [requirementRuleTypeId, setRequirementRuleTypeId] = useState("");

  const isRootOrganization =
    organizationId === Organizations.KnowfullyLearningGroup;

  const licenseResult = useStrictQuery(
    ["licenseService.getLicenseById", id, organizationId],
    () => licenseService.getLicenseById(id!),
    {
      enabled: !!id,
    },
  );

  const activeRequirementSetResult = useQuery(
    ["requirementSetService.getActiveRequirementSet", id],
    () => requirementSetService.getActiveRequirementSet(id!),
    { enabled: !!id },
  );

  const debouncedRequirementSearch = useDebounce(requirementSearch, 500);

  const requirementsRequest = ListRequestOfRequirementAdminFilter.fromJS({
    pageNumber: 0,
    pageSize: 200,
    parameters: RequirementAdminFilter.fromJS({
      searchText: debouncedRequirementSearch || undefined,
      ruleTypeId: requirementRuleTypeId || undefined,
    }),
  });

  const requirementsResult = useQuery(
    ["requirementService.getRequirementsByLicense", id, requirementsRequest],
    () => requirementService.getRequirementsByLicense(id!, requirementsRequest),
    { enabled: !!id, keepPreviousData: true },
  );

  const ruleTypeOptionsResult = useRequirementFilterOptions();

  const license = licenseResult.data?.data;
  useTitle(license?.displayName || "License Details");

  if (!id) return <Navigate to={licensesUrl()} replace />;
  if (licenseResult.isLoading) return <Loader />;

  if (!license) {
    return (
      <>
        <RedirectBackButton
          redirectRoute={licensesUrl()}
          labelKey="backToListButtonLabel"
        />
        <Alert severity="info">{t({ id: "licenseNotAccessible" })}</Alert>
      </>
    );
  }

  const placeholder = (
    <Typography
      component="span"
      sx={{ color: "text.disabled", fontStyle: "italic", fontSize: "smaller" }}
    >
      {t({ id: "selectABrandOrganization" })}
    </Typography>
  );

  const withAcronym = (name?: string, acronym?: string) =>
    acronym ? `${name} (${acronym})` : name ?? "-";

  const activeRequirementSet = activeRequirementSetResult.data?.data;
  const ruleTypes = ruleTypeOptionsResult.data?.data?.ruleTypes ?? [];

  const rows: Array<{ label: string; node: ReactNode }> = [
    { label: t({ id: "licenseIdLabel" }), node: license.id },
    { label: t({ id: "displayNameLabel" }), node: license.displayName ?? "-" },
    {
      label: t({ id: "designation" }),
      node: withAcronym(license.designationName, license.designationAcronym),
    },
    {
      label: t({ id: "allProfessions" }),
      node:
        license.professions && license.professions.length > 0 ? (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {license.professions.map((p, i) => (
              <Chip key={i} size="small" label={p} />
            ))}
          </Box>
        ) : (
          "-"
        ),
    },
    { label: t({ id: "locationLabel" }), node: license.locationName ?? "-" },
    {
      label: t({ id: "locationTypeLabel" }),
      node: license.locationTypeName ?? "-",
    },
    {
      label: t({ id: "licensureBody" }),
      node: withAcronym(
        license.licensureBodyName,
        license.licensureBodyAcronym,
      ),
    },
    {
      label: t({ id: "accreditationBodyLabel" }),
      node: withAcronym(
        license.accreditationBodyName,
        license.accreditationBodyAcronym,
      ),
    },
    {
      label: t({ id: "submissionAuthority" }),
      node: license.submissionAuthorityName ?? "-",
    },
    {
      label: t({ id: "activeRequirementSet" }),
      node: activeRequirementSet ? (
        <Link
          component={RouterLink}
          to={requirementSetDetailsUrl(activeRequirementSet.id)}
        >
          {activeRequirementSet.name}
        </Link>
      ) : activeRequirementSetResult.isError ? (
        t({ id: "activeRequirementSetLoadFailed" })
      ) : (
        "-"
      ),
    },
    {
      label: t({ id: "sequenceLabel" }),
      node: isRootOrganization ? placeholder : license.sequence ?? "-",
    },
    {
      label: t({ id: "alwaysShown" }),
      node: isRootOrganization
        ? placeholder
        : license.isAlwaysShown
        ? t({ id: "yes" })
        : t({ id: "no" }),
    },
  ];

  const handleEdit = (row: ILicenseQuestionDetail) => {
    setEditRow(row);
    editOpenActions.setTrue();
  };

  const handleDeleteClick = (row: ILicenseQuestionDetail) => {
    setDeleteRow(row);
    deleteOpenActions.setTrue();
  };

  const handleSaved = () => {
    queryClient.invalidateQueries(["licenseService.getLicenseById"]);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "licenseQuestionSaved" })),
    );
  };

  const handleDelete = async () => {
    if (deleteRow?.organizationLicenseQuestionId) {
      await licenseService.deleteLicenseQuestion(
        deleteRow.organizationLicenseQuestionId,
      );
      queryClient.invalidateQueries(["licenseService.getLicenseById"]);
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "licenseQuestionDeleted" })),
      );
    }
    deleteOpenActions.setFalse();
  };

  // License questions keep their own claim. Since KONE-24754 this page also opens to the
  // requirement-authoring roles, which do not hold it, so their edit and delete actions are withheld
  // rather than offered and then refused with a 403.
  const canEditLicenseQuestions = hasUserPermission(
    ParentClaim.Accreditation,
    ClaimResource.Licenses,
    ClaimPermission.Edit
  );

  const questionRows = (license.questions ?? []).map((q, i) => ({
    ...q,
    id: q.id ?? String(i),
    onEdit: canEditLicenseQuestions ? () => handleEdit(q) : undefined,
    onDelete:
      canEditLicenseQuestions &&
      !isRootOrganization &&
      q.organizationLicenseQuestionId
        ? () => handleDeleteClick(q)
        : undefined,
  }));

  const questionCells: Array<TableCellType> = [
    {
      accessor: "question",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "questionLabel" }),
    },
    {
      accessor: "type",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "typeLabel" }),
    },
    {
      accessor: "required",
      noSort: true,
      align: "left",
      type: "node",
      label: t({ id: "requiredLabel" }),
      View: RequiredCell,
    },
    {
      accessor: "defaultValue",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "defaultValueLabel" }),
    },
    {
      accessor: "validationRuleRegex",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "validationRule" }),
    },
    {
      accessor: "inheritedFrom",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "inheritedFrom" }),
    },
    {
      accessor: "actions",
      noSort: true,
      align: "right",
      type: "node",
      label: "",
      View: ActionsCell,
    },
  ];

  const requirementRows = (
    (requirementsResult.data?.data as IRequirementListItem[]) ?? []
  ).map((r, i) => ({
    ...r,
    id: r.id ?? String(i),
    ruleTypeName: r.ruleTypeName ?? "-",
    measureTypeName: r.measureTypeName ?? "-",
    recurrenceTypeName: r.recurrenceTypeName ?? "-",
    constraintsSummary: r.constraintsSummary ?? "-",
    additiveDisplay: r.isAdditive ? t({ id: "yes" }) : t({ id: "no" }),
  }));

  const hasRequirementFilter = !!debouncedRequirementSearch || !!requirementRuleTypeId;
  const requirementsEmptyMessageId = requirementsResult.isError
    ? "requirementsLoadFailed"
    : hasRequirementFilter
      ? "noMatchingRequirements"
      : "noRequirements";

  const requirementCells: Array<TableCellType> = [
    {
      accessor: "name",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "ruleTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "ruleType" }),
    },
    {
      accessor: "measureTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "measureLabel" }),
    },
    {
      accessor: "amount",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "amountLabel" }),
    },
    {
      accessor: "recurrenceTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "recurrenceLabel" }),
    },
    {
      accessor: "constraintsSummary",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "constraintsLabel" }),
    },
    {
      accessor: "additiveDisplay",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "additiveLabel" }),
    },
  ];

  return (
    <>
      <RedirectBackButton
        redirectRoute={licensesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h1>{license.displayName}</h1>

      <Box mb={3} display="flex" justifyContent="center">
        <Tooltip title={t({ id: "authorAndPublishRequirements" })}>
          <Link
            component={RouterLink}
            to={licenseAuthoringUrl(id)}
            sx={{ textDecoration: "none" }}
          >
            <Fab color="primary">
              <PublishIcon />
            </Fab>
          </Link>
        </Tooltip>
      </Box>

      <Typography variant="h5" sx={{ margin: "20px 0" }}>
        {t({ id: "license" })}
      </Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row" width="30%">
                  <b>{row.label}</b>
                </TableCell>
                <TableCell>{row.node}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Accordion sx={{ mt: 3 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "licenseQuestions" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SortableTable
            cells={questionCells}
            rows={questionRows}
            dense
            defaultOrderBy="sequence"
          />
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "licensureRequirements" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label={t({ id: "ruleType" })}
                value={requirementRuleTypeId}
                onChange={(e) => setRequirementRuleTypeId(e.target.value)}
              >
                <MenuItem value="">{t({ id: "allLabel" })}</MenuItem>
                {ruleTypes.map((ruleType) => (
                  <MenuItem key={ruleType.id} value={ruleType.id}>
                    {ruleType.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t({ id: "searchRequirementName" })}
                value={requirementSearch}
                onChange={(e) => setRequirementSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                  endAdornment: requirementSearch ? (
                    <IconButton onClick={() => setRequirementSearch("")}>
                      <ClearIcon />
                    </IconButton>
                  ) : null,
                }}
              />
            </Grid>
          </Grid>

          {requirementRows.length > 0 ? (
            <SortableTable
              // Remounting on a filter change resets the table's own page index,
              // which it never clears when the row set shrinks under it.
              key={`${debouncedRequirementSearch}|${requirementRuleTypeId}`}
              cells={requirementCells}
              rows={requirementRows}
              dense
              defaultOrderBy="sortOrder"
              contextMenuItems={[
                OpenLinkInNewTabContextMenuItem((requirementId) =>
                  requirementDetailsUrl(requirementId),
                ),
              ]}
              onClick={(requirementId: string) =>
                navigate(requirementDetailsUrl(requirementId))
              }
            />
          ) : (
            <Alert severity={requirementsResult.isError ? "error" : "info"}>
              {t({ id: requirementsEmptyMessageId })}
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {editRow && (
        <LicenseQuestionForm
          licenseId={id}
          question={editRow}
          isRootOrganization={isRootOrganization}
          open={editOpen}
          onClose={editOpenActions.setFalse}
          onSaved={handleSaved}
        />
      )}

      <DeleteEntityDialog
        dialogTitleKey="licenseQuestionDeleteOverride"
        contentKey="licenseQuestionDeleteQuestion"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default LicenseDetails;
